import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Globe } from "@phosphor-icons/react";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Explainer } from "../components/friendly";

const Field = ({ label, hint, children }) => (
  <div>
    <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400 font-medium">{label}</p>
    {hint && <p className="text-[11px] text-slate-400 mt-0.5">{hint}</p>}
    <div className="mt-1.5">{children}</div>
  </div>
);

export default function BrandPage() {
  const { activeClientId, activeClient, loadClients, setActiveClientId } = useApp();
  const [brand, setBrand] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [newClient, setNewClient] = useState({ name: "", industry: "", website: "" });
  const [learnOpen, setLearnOpen] = useState(false);
  const [learnUrl, setLearnUrl] = useState("");
  const [learning, setLearning] = useState(false);

  const load = useCallback(() => {
    if (activeClientId) api.get(`/clients/${activeClientId}/brand`).then((r) => setBrand(r.data)).catch(() => setBrand(null));
  }, [activeClientId]);
  useEffect(load, [load]);

  const set = (k) => (e) => setBrand({ ...brand, [k]: e.target.value });
  const setList = (k) => (e) => setBrand({ ...brand, [k]: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) });

  const save = async () => {
    setSaving(true);
    try {
      const { profile_id, client_id, org_id, updated_at, ...body } = brand;
      body.target_cadence = Object.fromEntries(Object.entries(body.target_cadence || {}).map(([k, v]) => [k, Number(v) || 0]));
      await api.put(`/clients/${activeClientId}/brand`, body);
      toast.success("Brand Intelligence saved — all AI output now uses it");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const learn = async () => {
    if (!learnUrl.trim()) return toast.error("Enter a website URL");
    setLearning(true);
    try {
      const { data } = await api.post("/ai/learn-brand", { client_id: activeClientId, url: learnUrl.trim() });
      const s = data.suggestions || {};
      setBrand({
        ...brand,
        voice: s.voice || brand.voice,
        guidelines: s.guidelines || brand.guidelines,
        audience: s.audience || brand.audience,
        keywords: [...new Set([...(brand.keywords || []), ...(s.keywords || [])])],
        competitors: [...new Set([...(brand.competitors || []), ...(s.competitors || [])])],
      });
      setLearnOpen(false);
      toast.success("Profile drafted from the website — review the fields and hit Save");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Learning failed");
    } finally { setLearning(false); }
  };

  const createClient = async () => {
    if (!newClient.name.trim()) return toast.error("Client name required");
    const { data } = await api.post("/clients", newClient);
    await loadClients();
    setActiveClientId(data.client_id);
    setNewOpen(false);
    setNewClient({ name: "", industry: "", website: "" });
    toast.success("Client onboarded — build their brand profile");
  };

  return (
    <div className="space-y-6 fade-up max-w-3xl" data-testid="brand-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-3xl tracking-tight">Brand Intelligence</h1>
          <p className="text-sm text-slate-500 mt-1">The living brain for <b>{activeClient?.name || "—"}</b>. Every AI draft is grounded here.</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="learn-brand-button" variant="outline" className="rounded-lg gap-2"
            onClick={() => { setLearnUrl(activeClient?.website || ""); setLearnOpen(true); }}>
            <Globe size={14} /> Learn from website (1 credit)
          </Button>
          <Button data-testid="new-client-button" variant="outline" className="rounded-lg gap-2" onClick={() => setNewOpen(true)}><Plus size={14} /> New client</Button>
        </div>
      </div>
      <Explainer id="brand">This is what makes the AI sound like you and not like a robot. Voice, rules, audience, banned words — every draft is grounded in what you write here. The fuller it is, the better every draft gets.</Explainer>

      {brand && (
        <div className="bg-white border border-slate-200/80 rounded-xl p-6 sm:p-8 card-float space-y-6">
          <Field label="Voice & Tone" hint="How the brand sounds. Person, rhythm, humor, dos & don'ts.">
            <Textarea data-testid="brand-voice-input" rows={3} value={brand.voice || ""} onChange={set("voice")} />
          </Field>
          <Field label="Content Guidelines" hint="What to always/never include, visual direction, formats.">
            <Textarea data-testid="brand-guidelines-input" rows={3} value={brand.guidelines || ""} onChange={set("guidelines")} />
          </Field>
          <Field label="Legal & Ad Rules" hint="Regulated claims, disclaimers, compliance requirements.">
            <Textarea data-testid="brand-legal-input" rows={2} value={brand.legal_rules || ""} onChange={set("legal_rules")} />
          </Field>
          <Field label="Target Audience">
            <Textarea data-testid="brand-audience-input" rows={2} value={brand.audience || ""} onChange={set("audience")} />
          </Field>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Keywords / Topics" hint="Comma-separated">
              <Input data-testid="brand-keywords-input" value={(brand.keywords || []).join(", ")} onChange={setList("keywords")} />
            </Field>
            <Field label="Banned Words & Claims" hint="Safeguards block these. Comma-separated">
              <Input data-testid="brand-banned-input" value={(brand.banned_words || []).join(", ")} onChange={setList("banned_words")} />
            </Field>
            <Field label="Competitors" hint="Never promoted. Comma-separated">
              <Input data-testid="brand-competitors-input" value={(brand.competitors || []).join(", ")} onChange={setList("competitors")} />
            </Field>
            <Field label="Brand Colors" hint="Hex codes, comma-separated — used by AI image gen">
              <Input data-testid="brand-colors-input" value={(brand.brand_colors || []).join(", ")} onChange={setList("brand_colors")} />
            </Field>
            <Field label="Languages" hint="Content languages, comma-separated (e.g. en, es, de)">
              <Input data-testid="brand-languages-input" value={(brand.languages || []).join(", ")} onChange={setList("languages")} />
            </Field>
          </div>
          <Field label="Target Celebre" hint="Posts per week, per platform — tracked on the Calendar">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-1">
              {["linkedin", "instagram", "x", "tiktok", "facebook"].map((p) => (
                <div key={p}>
                  <p className="text-[11px] text-slate-400 capitalize mb-1">{p === "x" ? "X" : p}</p>
                  <Input data-testid={`brand-cadence-${p}`} type="number" min="0" value={brand.target_cadence?.[p] ?? ""}
                    onChange={(e) => setBrand({ ...brand, target_cadence: { ...(brand.target_cadence || {}), [p]: e.target.value === "" ? 0 : parseInt(e.target.value, 10) } })}
                    placeholder="0" />
                </div>
              ))}
            </div>
          </Field>
          {brand.brand_colors?.length > 0 && (
            <div className="flex gap-2">{brand.brand_colors.map((c, i) => <span key={i} className="w-8 h-8 rounded-lg border border-slate-200" style={{ background: c }} title={c} />)}</div>
          )}
          <div className="flex justify-end border-t border-slate-100 pt-5">
            <Button data-testid="brand-save-button" onClick={save} disabled={saving} className="rounded-lg bg-[hsl(9,69%,62%)] hover:bg-[hsl(8,54%,54%)]">{saving ? "Saving…" : "Save profile"}</Button>
          </div>
        </div>
      )}

      <Dialog open={learnOpen} onOpenChange={setLearnOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-display">Learn the brand from a website</DialogTitle></DialogHeader>
          <p className="text-[13px] text-slate-500">The AI reads the site and drafts voice, guidelines, audience, keywords and competitors. You review before saving — nothing is overwritten silently.</p>
          <Input data-testid="learn-url-input" placeholder="https://client-website.com" value={learnUrl} onChange={(e) => setLearnUrl(e.target.value)} />
          <Button data-testid="learn-submit-button" onClick={learn} disabled={learning} className="w-full rounded-lg bg-[hsl(9,69%,62%)] hover:bg-[hsl(8,54%,54%)]">
            {learning ? "Reading the site…" : "Analyze website (1 credit)"}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-display">Onboard a client</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="new-client-name" placeholder="Client / brand name" value={newClient.name} onChange={(e) => setNewClient({ ...newClient, name: e.target.value })} />
            <Input data-testid="new-client-industry" placeholder="Industry" value={newClient.industry} onChange={(e) => setNewClient({ ...newClient, industry: e.target.value })} />
            <Input data-testid="new-client-website" placeholder="Website" value={newClient.website} onChange={(e) => setNewClient({ ...newClient, website: e.target.value })} />
            <Button data-testid="new-client-submit" onClick={createClient} className="w-full rounded-lg bg-slate-900 hover:bg-slate-700">Create client</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
